import { CellContext } from '@tanstack/react-table';
import { TestAssignment } from '../../../api/admin';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  TooltipDetail,
  TooltipTitle,
  TooltipContent,
} from '../Progress/Tooltip';

dayjs.extend(utc);

const StatusPill = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  ${({ status, theme }) => {
    switch (status) {
      case 'Assessed':
        return css`
          background-color: ${theme.colors.buttonPrimary}1A;
          border: 1px solid ${theme.colors.buttonPrimary}1A;
          color: ${theme.colors.buttonPrimary};
        `;
      case 'Completed':
        return css`
          background-color: ${theme.colors.highlight}1A;
          border: 1px solid ${theme.colors.highlight}1A;
          color: ${theme.colors.highlight};
        `;
      case 'In Progress':
        return css`
          background-color: ${theme.colors.buttonSecondary}1A;
          border: 1px solid ${theme.colors.buttonSecondary}1A;
          color: ${theme.colors.buttonSecondary};
        `;

      default:
        return css`
          background-color: ${theme.colors.textPrimary}1A;
          border: 1px solid ${theme.colors.textPrimary}1A;
          color: ${theme.colors.textPrimary};
        `;
    }
  }}
`;

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const TimelineRow = styled.div<{ isReached: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  opacity: ${({ isReached }) => (isReached ? 1 : 0.45)};
`;

const TimelineLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const TimelineDot = styled.span<{ isReached: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
  background-color: ${({ isReached, theme }) =>
    isReached ? theme.colors.buttonPrimary : theme.colors.textPlaceholder};
`;

const TimelineTime = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textActive};
  white-space: nowrap;
`;

const formatTimelineDate = (date: Date | null | undefined) =>
  date ? dayjs.utc(date).format('DD MMM, HH:mm') : '-';

export default function StatusCell(info: CellContext<TestAssignment, unknown>) {
  const { invitedAt, startedAt, finishedAt, reviewedAt } = info.row.original;

  const isFinished = finishedAt;
  const isReviewed = reviewedAt;
  const isStarted = startedAt;

  const status = isReviewed
    ? 'Assessed'
    : isFinished
    ? 'Completed'
    : isStarted
    ? 'In Progress'
    : 'Invited';

  const tooltipCopy = isReviewed
    ? 'Results are ready to be viewed'
    : isFinished
    ? 'Tester has completed the test. Test results are being computed.'
    : isStarted
    ? 'Tester has started the test.'
    : 'Tester has access to the test';
  const uniqueId = `status-tooltip-${info.row.id}`;

  const timeline: Array<{ label: string; date: Date | null }> = [
    { label: 'Invited', date: invitedAt ?? null },
    { label: 'Started', date: startedAt ?? null },
    { label: 'Completed', date: finishedAt ?? null },
    { label: 'Reviewed', date: reviewedAt ?? null },
  ];

  return (
    <>
      <StatusPill status={status} data-tooltip-id={uniqueId}>
        {status}
      </StatusPill>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>{status}</TooltipTitle>
          <TooltipDetail>{tooltipCopy}</TooltipDetail>
          <TimelineWrapper>
            {timeline.map((item) => (
              <TimelineRow key={item.label} isReached={!!item.date}>
                <TimelineLabel>
                  <TimelineDot isReached={!!item.date} />
                  {item.label}
                </TimelineLabel>
                <TimelineTime>{formatTimelineDate(item.date)}</TimelineTime>
              </TimelineRow>
            ))}
          </TimelineWrapper>
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}
